import React, { useRef, useState } from "react";
import { Box, Divider, IconButton, makeStyles } from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import GetAppIcon from "@material-ui/icons/GetApp";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { useSnackbar } from "notistack";
import { PrimaryButton } from "../PrimaryButton";
import { Body1, Caption, H6 } from "../Typography";
import { colorsLight } from "../../themes";

const useStyles = makeStyles((theme) => ({
  dropzone: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1.25),
    padding: theme.spacing(4.5, 3),
    backgroundColor: colorsLight.background.light,
    border: `1px dashed ${colorsLight.borders.primary}`,
    borderRadius: 10,
    textAlign: "center",
  },
  dropzoneActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: colorsLight.background.notificationBackground,
  },
  helperText: {
    fontSize: 14,
    color: colorsLight.font.tertiaryAlt,
  },
  fileList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    marginTop: theme.spacing(3),
  },
  fileRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  fileText: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    minWidth: 0,
    wordBreak: "break-word",
  },
  fileName: {
    color: colorsLight.font.main,
  },
  fileDescription: {
    color: colorsLight.font.light,
  },
  actions: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
  },
  actionButton: {
    color: colorsLight.font.light,
  },
  primaryBtn: {
    padding: theme.spacing(1.5, 3),
  },
}));

export interface FileUploadProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
  acceptedExtensions: string[];
  maxSizeBytes: number;
  maxFiles?: number;
  disabled?: boolean;
  dataTestId?: string;
  getTypeErrorMessage?: (file: File, acceptedExtensions: string[]) => string;
  getSizeErrorMessage?: (file: File, maxSizeBytes: number) => string;
  getMaxFilesErrorMessage?: (maxFiles: number) => string;
  getFileDescription?: (file: File, index: number) => React.ReactNode;
  onView?: (file: File, index: number) => void;
  onDownload?: (file: File, index: number) => void;
  renderProgress?: (file: File, index: number) => React.ReactNode;
  renderError?: (file: File, index: number) => React.ReactNode;
}

const trimNumber = (value: number) => String(parseFloat(value.toFixed(2)));

export const formatFileSize = (size: number) => {
  if (size >= 1048576) {
    return `${trimNumber(size / 1048576)} MB`;
  }
  if (size >= 1024) {
    return `${trimNumber(size / 1024)} KB`;
  }
  return `${size} bytes`;
};

const extensionLabels = (acceptedExtensions: string[]) =>
  acceptedExtensions.map((extension) => extension.replace(/^\./, "").toUpperCase());

const joinWithOr = (items: string[]) =>
  items.length <= 2 ? items.join(" or ") : `${items.slice(0, -1).join(", ")}, or ${items[items.length - 1]}`;

const defaultTypeError = (_file: File, acceptedExtensions: string[]) =>
  `File type not accepted. Files must be ${joinWithOr(extensionLabels(acceptedExtensions))}.`;

const defaultSizeError = (_file: File, maxSizeBytes: number) =>
  `File too large. Max ${formatFileSize(maxSizeBytes)} per file.`;

const defaultMaxFilesError = (maxFiles: number) =>
  `You can upload at most ${maxFiles} ${maxFiles === 1 ? "file" : "files"}`;

const hasAcceptedExtension = (file: File, acceptedExtensions: string[]) => {
  const name = file.name.toLowerCase();
  return acceptedExtensions.some((extension) => name.endsWith(extension.toLowerCase()));
};

const FileUpload: React.FC<FileUploadProps> = ({
  files,
  onFilesChange,
  acceptedExtensions,
  maxSizeBytes,
  maxFiles,
  disabled = false,
  dataTestId = "file-upload",
  getTypeErrorMessage = defaultTypeError,
  getSizeErrorMessage = defaultSizeError,
  getMaxFilesErrorMessage = defaultMaxFilesError,
  getFileDescription,
  onView,
  onDownload,
  renderProgress,
  renderError,
}) => {
  const classes = useStyles();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const addFiles = (candidates: File[]) => {
    const nextErrors: string[] = [];
    const accepted: File[] = [];
    let limitReached = false;

    candidates.forEach((file) => {
      if (!hasAcceptedExtension(file, acceptedExtensions)) {
        nextErrors.push(getTypeErrorMessage(file, acceptedExtensions));
      } else if (file.size > maxSizeBytes) {
        nextErrors.push(getSizeErrorMessage(file, maxSizeBytes));
      } else if (maxFiles !== undefined && files.length + accepted.length >= maxFiles) {
        limitReached = true;
      } else {
        accepted.push(file);
      }
    });

    if (limitReached && maxFiles !== undefined) {
      nextErrors.push(getMaxFilesErrorMessage(maxFiles));
    }
    new Set(nextErrors).forEach((message) => enqueueSnackbar(message, { variant: "error", preventDuplicate: true }));
    if (accepted.length > 0) {
      onFilesChange([...files, ...accepted]);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
    if (disabled) {
      return;
    }
    addFiles(Array.from(event.dataTransfer.files));
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  const handleSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    addFiles(event.target.files ? Array.from(event.target.files) : []);
    event.target.value = "";
  };

  const handleRemove = (index: number) => {
    onFilesChange(files.filter((_, i) => i !== index));
  };

  const allowMultiple = maxFiles === undefined || maxFiles > 1;

  return (
    <Box>
      <Box
        data-testid={`${dataTestId}-dropzone`}
        className={`${classes.dropzone} ${isDragOver ? classes.dropzoneActive : ""}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={() => setIsDragOver(false)}
      >
        <H6 component="p">Drag and drop files here</H6>
        <Caption className={classes.helperText}>or</Caption>
        <PrimaryButton className={classes.primaryBtn} disabled={disabled} onClick={() => inputRef.current?.click()}>
          Browse Documents
        </PrimaryButton>
        <Caption className={classes.helperText}>
          {`Accepted: ${extensionLabels(acceptedExtensions).join(", ")} — Max ${formatFileSize(maxSizeBytes)} per file`}
        </Caption>
        <input
          hidden
          type="file"
          ref={inputRef}
          accept={acceptedExtensions.join(",")}
          multiple={allowMultiple}
          disabled={disabled}
          onChange={handleSelect}
          data-testid={`${dataTestId}-input`}
        />
      </Box>
      {files.length > 0 && (
        <Box className={classes.fileList}>
          {files.map((file, index) => (
            <React.Fragment key={`${file.name}-${index}`}>
              {index > 0 && <Divider />}
              <Box>
                <Box className={classes.fileRow}>
                  <Box className={classes.fileText}>
                    <Body1 className={classes.fileName} data-testid={`${dataTestId}-file-name`}>
                      {file.name}
                    </Body1>
                    {getFileDescription && (
                      <Caption className={classes.fileDescription}>{getFileDescription(file, index)}</Caption>
                    )}
                  </Box>
                  <Box className={classes.actions}>
                    {onView && (
                      <IconButton
                        className={classes.actionButton}
                        aria-label={`View ${file.name}`}
                        disabled={disabled}
                        onClick={() => onView(file, index)}
                        data-testid={`${dataTestId}-view`}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    )}
                    {onDownload && (
                      <IconButton
                        className={classes.actionButton}
                        aria-label={`Download ${file.name}`}
                        disabled={disabled}
                        onClick={() => onDownload(file, index)}
                        data-testid={`${dataTestId}-download`}
                      >
                        <GetAppIcon />
                      </IconButton>
                    )}
                    <IconButton
                      className={classes.actionButton}
                      aria-label={`Remove ${file.name}`}
                      disabled={disabled}
                      onClick={() => handleRemove(index)}
                      data-testid={`${dataTestId}-remove`}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Box>
                {renderProgress?.(file, index)}
                {renderError?.(file, index)}
              </Box>
            </React.Fragment>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default FileUpload;
